/**
 * Provably-fair draw (TZ §7). Works in Node and the browser (Web Crypto), so the
 * Mini App "Verify fairness" page runs this exact code.
 *
 *  commit:  hash = sha256(seed)            — published 24 h before
 *  draw:    index = HMAC-SHA256(seed, label) mod total   (label = "season:<id>")
 *  verify:  anyone recomputes both from the published seed + snapshot.
 */

export interface SnapshotRange {
  userId: number;
  fromIdx: number; // inclusive
  toIdx: number; // inclusive
}

export interface Snapshot {
  ranges: SnapshotRange[];
  total: number;
}

export interface LedgerRow {
  id: number;
  userId: number;
  amount: number;
}

const subtle = () => {
  const s = globalThis.crypto?.subtle;
  if (!s) throw new Error('Web Crypto unavailable');
  return s;
};
const enc = new TextEncoder();
const hex = (buf: ArrayBuffer) =>
  [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');

/** 32 random bytes as hex. */
export function generateSeed(): string {
  const bytes = new Uint8Array(32);
  globalThis.crypto.getRandomValues(bytes);
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export async function sha256Hex(input: string): Promise<string> {
  return hex(await subtle().digest('SHA-256', enc.encode(input)));
}

export async function hmacHex(key: string, message: string): Promise<string> {
  const k = await subtle().importKey(
    'raw',
    enc.encode(key),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  return hex(await subtle().sign('HMAC', k, enc.encode(message)));
}

export const commitHash = sha256Hex;

/** Label for attempt n (1 = first draw; redraws append the attempt number). */
export function drawLabel(base: string, attempt = 1): string {
  return attempt <= 1 ? base : `${base}:attempt:${attempt}`;
}

/**
 * Freeze balances from the ledger at commit time. Rows with id > `upToEventId`
 * are ignored (tickets issued after commit count for the next draw).
 * Users are ordered by their first event id — the "sorted by event id" rule.
 * `weighting: 'equal'` gives every eligible holder exactly one slot.
 */
export function buildSnapshot(
  rows: LedgerRow[],
  opts: {
    upToEventId?: number;
    eligible?: (userId: number) => boolean;
    weighting?: 'tickets' | 'equal';
    exclude?: Iterable<number>;
  } = {},
): Snapshot {
  const excluded = new Set(opts.exclude ?? []);
  const perUser = new Map<number, { first: number; balance: number }>();
  for (const r of rows) {
    if (opts.upToEventId !== undefined && r.id > opts.upToEventId) continue;
    const cur = perUser.get(r.userId);
    if (cur) {
      cur.balance += r.amount;
      cur.first = Math.min(cur.first, r.id);
    } else perUser.set(r.userId, { first: r.id, balance: r.amount });
  }
  const holders = [...perUser.entries()]
    .filter(([uid, v]) => v.balance > 0 && !excluded.has(uid) && (opts.eligible?.(uid) ?? true))
    .sort((a, b) => a[1].first - b[1].first || a[0] - b[0]);

  const ranges: SnapshotRange[] = [];
  let next = 0;
  for (const [userId, v] of holders) {
    const n = opts.weighting === 'equal' ? 1 : v.balance;
    ranges.push({ userId, fromIdx: next, toIdx: next + n - 1 });
    next += n;
  }
  return { ranges, total: next };
}

/** Remove users from a frozen snapshot (redraw) and re-pack indexes in the same order. */
export function excludeFromSnapshot(snap: Snapshot, userIds: Iterable<number>): Snapshot {
  const ex = new Set(userIds);
  const ranges: SnapshotRange[] = [];
  let next = 0;
  for (const r of snap.ranges) {
    if (ex.has(r.userId)) continue;
    const n = r.toIdx - r.fromIdx + 1;
    ranges.push({ userId: r.userId, fromIdx: next, toIdx: next + n - 1 });
    next += n;
  }
  return { ranges, total: next };
}

export function ownerOf(snap: Snapshot, index: number): number | null {
  let lo = 0;
  let hi = snap.ranges.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const r = snap.ranges[mid]!;
    if (index < r.fromIdx) hi = mid - 1;
    else if (index > r.toIdx) lo = mid + 1;
    else return r.userId;
  }
  return null;
}

export async function winningIndex(seed: string, label: string, total: number): Promise<number> {
  if (total <= 0) throw new Error('empty snapshot');
  return Number(BigInt(`0x${await hmacHex(seed, label)}`) % BigInt(total));
}

export interface DrawResult {
  winnerUserId: number;
  ticketIndex: number;
  forced: boolean;
  label: string;
}

/**
 * Run (or redraw) a draw. Redraws pass the previous winners in `excluded` and the
 * attempt number; the snapshot is re-packed without them and the label changes.
 * `forcedUserId` (test mode only) returns that user's first ticket with forced=true.
 */
export async function runDraw(opts: {
  seed: string;
  baseLabel: string;
  snapshot: Snapshot;
  attempt?: number;
  excluded?: number[];
  forcedUserId?: number | null;
}): Promise<DrawResult> {
  const snap = opts.excluded?.length
    ? excludeFromSnapshot(opts.snapshot, opts.excluded)
    : opts.snapshot;
  const label = drawLabel(opts.baseLabel, opts.attempt);
  if (opts.forcedUserId != null) {
    const r = snap.ranges.find((x) => x.userId === opts.forcedUserId);
    if (!r) throw new Error('forced winner is not in the snapshot');
    return { winnerUserId: r.userId, ticketIndex: r.fromIdx, forced: true, label };
  }
  const ticketIndex = await winningIndex(opts.seed, label, snap.total);
  const winnerUserId = ownerOf(snap, ticketIndex);
  if (winnerUserId === null) throw new Error('index outside snapshot');
  return { winnerUserId, ticketIndex, forced: false, label };
}

/** Client-side verification for the "Verify fairness" button. */
export async function verifyDraw(opts: {
  seed: string;
  commitHash: string;
  baseLabel: string;
  snapshot: Snapshot;
  attempt?: number;
  excluded?: number[];
  claimedWinnerUserId: number;
}) {
  const hashOk = (await sha256Hex(opts.seed)) === opts.commitHash.toLowerCase();
  const result = await runDraw({ ...opts, forcedUserId: null });
  return {
    hashOk,
    winnerOk: result.winnerUserId === opts.claimedWinnerUserId,
    recomputed: result,
  };
}
